import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Checkbox,
    Typography,
} from '@mui/material';
import { Check, ChevronRight, Sparkles } from 'lucide-react';
import * as React from 'react';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import {
    getModalityColor,
    getModalityIcon,
    Modality,
} from '~/domain/dicom/modality';

interface ExportListItemProps {
    expanded: boolean;
    onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
    isSelected: boolean;
    onSelect: () => void;
    label: string;
    modality: Modality;
    isDerived: boolean;
    isExported: boolean;
    details: React.ReactNode;
    subtitle?: string;
    extraBadge?: React.ReactNode;
}

const ExportListItem: React.FC<ExportListItemProps> = ({
    expanded,
    onChange,
    isSelected,
    onSelect,
    label,
    modality,
    isDerived,
    isExported,
    details,
    subtitle,
    extraBadge,
}) => (
    <Accordion
        expanded={expanded}
        onChange={onChange}
        sx={(theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderBottom: 'none',
            ...(isExported && {
                backgroundColor: `${theme.palette.success.light}20`,
            }),
        })}
    >
        <AccordionSummary
            expandIcon={<ChevronRight size={16} />}
            sx={{ minHeight: 0, '& .MuiAccordionSummary-content': { my: 1 } }}
            aria-controls="panel-content"
            id="panel-header"
        >
            <Box display="flex" gap={1} alignItems="center" width="100%">
                <Checkbox
                    data-testid={`exportItemCheckbox${label}`}
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                    checked={isSelected}
                    onChange={onSelect}
                />
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                >
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                                flexShrink: 0,
                                color: getModalityColor(modality),
                            }}
                        >
                            {getModalityIcon(modality, 13)}
                            <Typography
                                component="span"
                                fontSize={11}
                                fontWeight={500}
                                color="inherit"
                            >
                                {modality}
                            </Typography>
                        </Box>
                        <Typography component="span" fontSize={14}>
                            <SensitiveInfoTypography>
                                {label}
                            </SensitiveInfoTypography>
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                        {subtitle && (
                            <Typography
                                component="span"
                                fontSize={11}
                                color="primary"
                            >
                                <SensitiveInfoTypography>
                                    {subtitle}
                                </SensitiveInfoTypography>
                            </Typography>
                        )}
                        {extraBadge}
                        {isDerived && (
                            <Box
                                title="Generated during the session"
                                display="flex"
                                alignItems="center"
                                color="warning.main"
                            >
                                <Sparkles size={14} color="currentColor" />
                            </Box>
                        )}
                        {isExported && (
                            <Typography
                                data-testid="export-item-exported-badge"
                                fontSize={12}
                                component="span"
                                color="success.main"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <Check size="14px" color="currentColor" />
                                Exported
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1, pl: 6 }}>{details}</AccordionDetails>
    </Accordion>
);

export default ExportListItem;
